import { Box, Button, Flex, Heading, HStack, SimpleGrid, Spinner, Tag, Text, VStack } from "@chakra-ui/react";
import React from "react";
import { Link } from "react-router-dom";
import { FiArrowRight, FiCalendar, FiClock, FiUser } from "react-icons/fi";
import { ApiVotingListItem, VOTING_STATUSES } from "@lubbezposrednio-frontend/core";

const formatDate = (iso?: string) =>
  iso ? new Date(iso).toLocaleDateString("pl-PL", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const organizerName = (user: ApiVotingListItem["user"]) =>
  user?.is_organization
    ? user?.organization_name || user?.email
    : [user?.first_name, user?.last_name].filter(Boolean).join(" ") || user?.email;

const StatusBadge: React.FC<{ status: VOTING_STATUSES }> = ({ status }) => {
  const isOngoing = status === VOTING_STATUSES.ONGOING;
  return (
    <Tag
      bg={isOngoing ? "rgba(47,206,37,0.15)" : "rgba(67,0,152,0.10)"}
      color={isOngoing ? "#1f9e18" : "primary"}
      fontWeight="700"
      fontSize="12px"
      px="10px"
      py="4px"
      borderRadius="full"
    >
      <Box as="span" w="7px" h="7px" borderRadius="full" bg={isOngoing ? "#2FCE25" : "primary"} mr="7px" />
      {isOngoing ? "Trwa" : "Planowane"}
    </Tag>
  );
};

const VotingCard: React.FC<{ voting: ApiVotingListItem }> = ({ voting }) => (
  <Box
    as={Link}
    to={`/glosowanie/${voting.id}`}
    bg="white"
    borderRadius="18px"
    border="1px solid rgba(67,0,152,0.08)"
    boxShadow="0 8px 24px rgba(67,0,152,0.06)"
    p="24px"
    transition="all 0.18s"
    _hover={{ transform: "translateY(-4px)", boxShadow: "0 16px 36px rgba(67,0,152,0.14)", borderColor: "rgba(67,0,152,0.18)" }}
  >
    <VStack align="stretch" spacing="16px" h="100%">
      <StatusBadge status={voting.status} />
      <Heading fontSize="19px" lineHeight="25px" color="primary" noOfLines={2} minH="50px">
        {voting.title}
      </Heading>
      <VStack align="stretch" spacing="8px" color="gray.600" fontSize="14px">
        <HStack spacing="8px">
          <FiUser size={15} />
          <Text noOfLines={1}>{organizerName(voting.user)}</Text>
        </HStack>
        <HStack spacing="8px">
          {voting.status === VOTING_STATUSES.ONGOING ? <FiClock size={15} /> : <FiCalendar size={15} />}
          <Text>
            {voting.status === VOTING_STATUSES.ONGOING
              ? `Do ${formatDate(voting.end_date)}`
              : `Od ${formatDate(voting.start_date)}`}
          </Text>
        </HStack>
      </VStack>
      <HStack spacing="6px" color="accent" fontWeight="700" fontSize="14px" pt="2px" mt="auto">
        <Text>Zobacz głosowanie</Text>
        <FiArrowRight size={16} />
      </HStack>
    </VStack>
  </Box>
);

interface VotingsShowcaseProps {
  title: string;
  subtitle?: string;
  votings: ApiVotingListItem[];
  isLoading: boolean;
  emptyText: string;
}

export const VotingsShowcase: React.FC<VotingsShowcaseProps> = ({ title, subtitle, votings, isLoading, emptyText }) => (
  <Box w="100%" maxW="1240px" mx="auto" px={{ base: "24px", md: "32px" }} py={{ base: "32px", md: "44px" }}>
    <Flex justify="space-between" align="center" mb="24px" flexWrap="wrap" gap="12px">
      <Box>
        <Heading fontSize={{ base: "26px", md: "32px" }} color="primary" fontWeight="800" letterSpacing="-0.01em">
          {title}
        </Heading>
        {subtitle && (
          <Heading as="p" fontSize="15px" fontWeight="500" color="gray.500" mt="4px">
            {subtitle}
          </Heading>
        )}
      </Box>
      <Button
        as={Link}
        to="/aktywne-glosowania"
        variant="ghost"
        color="accent"
        fontWeight="700"
        rightIcon={<FiArrowRight />}
        _hover={{ bg: "rgba(229,31,68,0.08)" }}
      >
        Zobacz wszystkie
      </Button>
    </Flex>

    {isLoading ? (
      <Flex justify="center" py="48px">
        <Spinner color="primary" size="lg" thickness="3px" />
      </Flex>
    ) : votings.length === 0 ? (
      <Flex
        justify="center"
        align="center"
        py="48px"
        bg="rgba(67,0,152,0.03)"
        border="1px dashed rgba(67,0,152,0.2)"
        borderRadius="18px"
      >
        <Text color="gray.500" fontSize="16px">
          {emptyText}
        </Text>
      </Flex>
    ) : (
      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing="20px">
        {votings.map(v => (
          <VotingCard key={v.id} voting={v} />
        ))}
      </SimpleGrid>
    )}
  </Box>
);
